import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, ClipboardList, MessageCircleQuestion, Factory, Package,
  Send, CalendarClock, AlertTriangle, Handshake, Trophy,
  DollarSign, Clock, RefreshCw, Loader2,
} from 'lucide-react';
import { getSourcingDashboardStats } from '../../lib/api';

const fmtUsd = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const STATUS_LABEL = {
  new_request: 'New Request',
  qualification: 'Qualification',
  awaiting_customer_specs: 'Awaiting Customer Specs',
  sourcing_inventory: 'Sourcing Inventory',
  proposal_preparation: 'Proposal Preparation',
  proposal_sent: 'Proposal Sent',
  follow_up: 'Follow-up',
  negotiation: 'Negotiation',
  ready_to_close: 'Ready to Close',
  won: 'Won',
  lost: 'Lost',
  on_hold: 'On Hold',
};

/** One counter card. */
const StatCard = ({ icon: Icon, label, value, tone = 'default', onClick, testid, hint }) => {
  const toneMap = {
    default: 'text-white',
    danger: 'text-red-300',
    warning: 'text-amber-300',
    info: 'text-[#00D4FF]',
    money: 'text-emerald-300',
    accent: 'text-purple-300',
    accent2: 'text-fuchsia-300',
    teal: 'text-teal-300',
    blue: 'text-blue-300',
    orange: 'text-orange-300',
  };
  const iconTone = {
    default: 'text-white/40',
    danger: 'text-red-400/70',
    warning: 'text-amber-400/70',
    info: 'text-[#00D4FF]/70',
    money: 'text-emerald-400/70',
    accent: 'text-purple-400/70',
    accent2: 'text-fuchsia-400/70',
    teal: 'text-teal-400/70',
    blue: 'text-blue-400/70',
    orange: 'text-orange-400/70',
  };
  const clickable = typeof onClick === 'function';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!clickable}
      className={`text-left bg-[#0A0A0A] border border-white/10 p-4 transition-colors ${
        clickable ? 'hover:border-white/25 hover:bg-white/[0.03] cursor-pointer' : 'cursor-default'
      }`}
      data-testid={testid}
      title={hint}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-3.5 h-3.5 ${iconTone[tone]}`} />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-white/50">{label}</span>
      </div>
      <div className={`text-3xl font-bold tabular-nums ${toneMap[tone]}`} data-testid={`${testid}-value`}>
        {value}
      </div>
    </button>
  );
};

/**
 * Sourcing Dashboard — Phase D2 workflow v2 widgets.
 *
 * 12 counters split into a stage row (top 5), pipeline row
 * (5 more) and a wider info row (Potential Revenue + Oldest
 * Active Opportunity). Clicks apply filters to the list below.
 */
export const SourcingDashboardWidgets = ({
  setStatusFilter, setFollowUpFilter,
  clearAllFilters, onOpenRequest,
}) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await getSourcingDashboardStats();
      setStats(r.data);
    } catch (e) {
      setStats(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = () => {
    setRefreshing(true);
    load();
  };

  const oldest = stats?.oldest_active_opportunity;

  const goToStatus = (st) => { clearAllFilters?.(); setStatusFilter?.(st); };

  if (loading) {
    return (
      <div className="mb-6" data-testid="sourcing-dashboard-loading">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="bg-[#0A0A0A] border border-white/10 p-4 h-[92px] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="mb-6 bg-[#0A0A0A] border border-white/10 p-4 text-white/50 text-sm" data-testid="sourcing-dashboard-error">
        Could not load sourcing dashboard stats. <button onClick={handleRefresh} className="text-[#00D4FF] hover:underline ml-1">Retry</button>
      </div>
    );
  }

  return (
    <div className="mb-6" data-testid="sourcing-dashboard-widgets">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-white/60">
          Today&apos;s Focus
        </h2>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-wider text-white/50 hover:text-white/80 disabled:opacity-40"
          data-testid="sourcing-dashboard-refresh"
        >
          {refreshing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      </div>

      {/* Row 1 — Early-stage pipeline */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <StatCard icon={FileText} label="New Requests" value={stats.new_requests}
          tone="info" testid="widget-new-requests"
          hint="Just came in — no work done yet."
          onClick={() => goToStatus('new_request')} />
        <StatCard icon={ClipboardList} label="Awaiting Qualification" value={stats.awaiting_qualification}
          tone="warning" testid="widget-awaiting-qualification"
          hint="Being triaged for fit / budget / feasibility."
          onClick={() => goToStatus('qualification')} />
        <StatCard icon={MessageCircleQuestion} label="Awaiting Customer Specs" value={stats.awaiting_customer_specs}
          tone="orange" testid="widget-awaiting-customer-specs"
          hint="Waiting on the customer to send models / quantities."
          onClick={() => goToStatus('awaiting_customer_specs')} />
        <StatCard icon={Factory} label="Currently Sourcing" value={stats.currently_sourcing}
          tone="accent" testid="widget-currently-sourcing"
          hint="Actively hunting inventory across suppliers."
          onClick={() => goToStatus('sourcing_inventory')} />
        <StatCard icon={Package} label="Proposal Preparation" value={stats.proposal_preparation}
          tone="accent2" testid="widget-proposal-preparation"
          hint="Options gathered, drafting the proposal."
          onClick={() => goToStatus('proposal_preparation')} />
      </div>

      {/* Row 2 — Sent & mid-cycle */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-3">
        <StatCard icon={Send} label="Proposals Sent" value={stats.proposals_sent}
          tone="info" testid="widget-proposals-sent"
          hint="Proposal delivered — awaiting first customer response."
          onClick={() => goToStatus('proposal_sent')} />
        <StatCard icon={CalendarClock} label="Follow-ups Due Today" value={stats.follow_ups_due_today}
          tone={stats.follow_ups_due_today > 0 ? 'warning' : 'default'}
          testid="widget-followups-due-today"
          hint="Follow-up scheduled for today. Click to filter."
          onClick={() => { clearAllFilters?.(); setFollowUpFilter?.('due_today'); }} />
        <StatCard icon={AlertTriangle} label="Overdue Follow-ups" value={stats.overdue_follow_ups}
          tone={stats.overdue_follow_ups > 0 ? 'danger' : 'default'}
          testid="widget-overdue-followups"
          hint="Follow-up date is in the past. Click to filter."
          onClick={() => { clearAllFilters?.(); setFollowUpFilter?.('overdue'); }} />
        <StatCard icon={Handshake} label="Negotiations" value={stats.negotiations}
          tone="warning" testid="widget-negotiations"
          hint="Actively negotiating price or terms."
          onClick={() => goToStatus('negotiation')} />
        <StatCard icon={Trophy} label="Ready to Close" value={stats.ready_to_close}
          tone="teal" testid="widget-ready-to-close"
          hint="Customer signalled clear purchase intent."
          onClick={() => goToStatus('ready_to_close')} />
      </div>

      {/* Row 3 — Value + oldest */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-3 mt-3">
        <div
          className="col-span-2 bg-[#0A0A0A] border border-white/10 p-4"
          data-testid="widget-potential-revenue"
          title="Sum of Estimated Value across all active (non-won/non-lost) opportunities."
        >
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-3.5 h-3.5 text-emerald-400/70" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-white/50">
              Potential Revenue
            </span>
          </div>
          <div className="text-3xl font-bold tabular-nums text-emerald-300"
            data-testid="widget-potential-revenue-value">
            {fmtUsd(stats.potential_revenue)}
          </div>
          <div className="text-[11px] text-white/40 mt-1">
            Σ estimated value across {stats.open_requests} active opportunity{stats.open_requests === 1 ? '' : 'ies'}
          </div>
        </div>

        <button
          type="button"
          onClick={() => oldest?.id && onOpenRequest?.(oldest.id)}
          disabled={!oldest?.id}
          className={`col-span-2 text-left bg-[#0A0A0A] border border-white/10 p-4 transition-colors ${
            oldest?.id ? 'hover:border-white/25 hover:bg-white/[0.03] cursor-pointer' : 'cursor-default opacity-70'
          }`}
          data-testid="widget-oldest-active"
          title={oldest?.id ? 'Click to open this opportunity.' : 'No active opportunities.'}
        >
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-3.5 h-3.5 text-white/40" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-white/50">
              Oldest Active Opportunity
            </span>
          </div>
          {oldest ? (
            <>
              <div className="text-lg font-semibold text-white truncate" data-testid="widget-oldest-active-company">
                {oldest.company || oldest.name || '—'}
              </div>
              <div className="text-[11px] text-white/50 mt-1 flex items-center gap-2 flex-wrap">
                <span className="text-amber-300 font-medium tabular-nums" data-testid="widget-oldest-active-days">
                  {oldest.days_open} day{oldest.days_open === 1 ? '' : 's'} open
                </span>
                <span className="text-white/25">·</span>
                <span>{STATUS_LABEL[oldest.status] || oldest.status || '—'}</span>
              </div>
            </>
          ) : (
            <div className="text-lg text-white/40">—</div>
          )}
        </button>
      </div>
    </div>
  );
};

export default SourcingDashboardWidgets;
